import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { goToSection, isHomeSectionId, type HomeSectionId } from '../navigation/goToSection';
import { LANGUAGES, setSiteLanguage, type SiteLanguage } from '../navigation/language';
import Logo from './ui/Logo';

// Home is the logo; Download is the "Get the app" button. Each item is named by its visible,
// translated label (an English aria-label would not match the Swahili or Luganda text).
const NAV_ITEMS: { id: HomeSectionId; labelKey: string }[] = [
  { id: 'how-it-works', labelKey: 'nav.how' },
  { id: 'calculator', labelKey: 'nav.fees' },
  { id: 'where-we-send', labelKey: 'nav.where' },
  { id: 'faq', labelKey: 'nav.faq' },
];

const SCROLLED_AFTER_PX = 8;

const languages = LANGUAGES;

function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function sectionFromLocation(pathname: string, hash: string): HomeSectionId | null {
  if (pathname !== '/') return null;
  const raw = hash.startsWith('#') ? decodeURIComponent(hash.slice(1)) : '';
  if (raw === '' || raw === 'home') return 'home';
  return isHomeSectionId(raw) ? raw : null;
}

function closedBarHeight(row: HTMLElement, appbar: HTMLElement | null): number {
  const style = appbar ? window.getComputedStyle(appbar) : null;
  const padTop = style ? Number.parseFloat(style.paddingTop) : 16;
  const padBottom = style ? Number.parseFloat(style.paddingBottom) : 16;
  const pad = (Number.isFinite(padTop) ? padTop : 16) + (Number.isFinite(padBottom) ? padBottom : 16);
  const height = row.getBoundingClientRect().height + pad;
  return height > 0 ? height : 72;
}

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState<HomeSectionId | null>(() =>
    sectionFromLocation(pathname, hash),
  );
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const menuWasOpen = useRef(false);
  const pendingSection = useRef<HomeSectionId | null>(null);

  const currentLang = languages.find((lang) => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    setCurrentSection(sectionFromLocation(pathname, hash));
  }, [pathname, hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLLED_AFTER_PX);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The sheet takes focus on open and hands it back to the menu button on close.
  useEffect(() => {
    if (menuOpen) {
      closeButtonRef.current?.focus();
    } else if (menuWasOpen.current) {
      menuButtonRef.current?.focus();
    }
    menuWasOpen.current = menuOpen;
  }, [menuOpen]);

  useEffect(() => {
    if (pathname !== '/') return undefined;

    const row = rowRef.current;
    // The calculator card lives inside the hero, so it is not a scroll-spy target: "Fees" would
    // otherwise be underlined on page load. It is still marked current after a click or #calculator.
    const targets = NAV_ITEMS
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el instanceof HTMLElement && el.closest('#home') == null);
    if (!row || targets.length === 0) return undefined;

    const latest = new Map<string, IntersectionObserverEntry>();
    let observer: IntersectionObserver | null = null;

    const publish = () => {
      if (latest.size < targets.length) return;
      const visible: IntersectionObserverEntry[] = [];
      latest.forEach((entry) => {
        if (entry.isIntersecting) visible.push(entry);
      });
      if (visible.length === 0) {
        setCurrentSection(null);
        return;
      }
      // The section filling most of the band wins, by visible height (WU-REACH: a share of the section would
      // favour short sections over the tall "Global reach" one).
      visible.sort((a, b) => {
        if (b.intersectionRect.height !== a.intersectionRect.height) {
          return b.intersectionRect.height - a.intersectionRect.height;
        }
        return a.boundingClientRect.top - b.boundingClientRect.top;
      });
      const id = visible[0].target.id;
      if (!isHomeSectionId(id)) return;
      setCurrentSection((prev) => (prev === id ? prev : id));
    };

    const connect = () => {
      observer?.disconnect();
      latest.clear();
      const appbar = row.closest('header');
      const height = closedBarHeight(row, appbar instanceof HTMLElement ? appbar : null);
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => latest.set(entry.target.id, entry));
          publish();
        },
        {
          root: null,
          rootMargin: `-${Math.round(height)}px 0px -45% 0px`,
          // Fine steps, so a tall section still reports as it scrolls through the band.
          threshold: Array.from({ length: 21 }, (_, step) => step / 20),
        },
      );
      targets.forEach((el) => observer?.observe(el));
    };

    connect();
    const resize = new ResizeObserver(() => connect());
    resize.observe(row);

    return () => {
      observer?.disconnect();
      resize.disconnect();
    };
  }, [pathname]);

  useEffect(() => {
    if (menuOpen) return;
    const id = pendingSection.current;
    if (id == null) return;
    pendingSection.current = null;
    goToSection(id, pathname, navigate);
  }, [menuOpen, pathname, navigate]);

  useEffect(() => {
    if (!menuOpen && !langOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      setLangOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen, langOpen]);

  useEffect(() => {
    if (!langOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const root = headerRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [langOpen]);

  const chooseSection = (id: HomeSectionId) => {
    setLangOpen(false);
    if (!menuOpen) {
      goToSection(id, pathname, navigate);
      return;
    }
    pendingSection.current = id;
    setMenuOpen(false);
  };

  const chooseLanguage = (code: SiteLanguage) => {
    setSiteLanguage(i18n, code);
    setLangOpen(false);
  };

  const openLanguage = () => {
    setLangOpen(true);
  };

  const onLanguageHover = () => {
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      setLangOpen(true);
    }
  };

  return (
    <header className={scrolled ? 'appbar appbar--scrolled' : 'appbar'}>
      <div ref={headerRef} className="relative w-full min-w-0">
        <div
          ref={rowRef}
          className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4"
        >
          <Link to="/" aria-label="LulPay Home" className="ui-logo-link shrink-0">
            <Logo size="sm" />
          </Link>

          <nav
            className="hidden min-w-0 items-center justify-center gap-1 lg:flex"
            aria-label="Sections"
          >
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                className="appbar__nav-btn"
                aria-current={currentSection === item.id ? true : undefined}
                onClick={() => chooseSection(item.id)}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </nav>

          <div className="flex shrink-0 items-center justify-self-end gap-2">
            <button
              ref={menuButtonRef}
              type="button"
              className="appbar__menu-btn lg:hidden"
              aria-expanded={menuOpen}
              aria-controls="header-section-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => {
                setLangOpen(false);
                setMenuOpen((open) => !open);
              }}
            >
              <MenuIcon />
            </button>

            <button
              type="button"
              className="appbar__lang-btn"
              aria-label={`Language: ${currentLang.name}`}
              aria-expanded={langOpen}
              aria-controls="header-language-menu"
              onClick={openLanguage}
              onMouseEnter={onLanguageHover}
            >
              <span aria-hidden="true">{currentLang.flag}</span>
              <span className="hidden lg:inline">{currentLang.name}</span>
            </button>

            <button
              type="button"
              className="btn btn--primary appbar__download"
              onClick={() => chooseSection('download')}
            >
              {t('nav.getApp')}
            </button>
          </div>
        </div>

        {langOpen && (
          <div id="header-language-menu" className="appbar__lang-menu" role="group" aria-label="Choose language">
            {languages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => chooseLanguage(lang.code)}
                className="appbar__lang-option"
                aria-pressed={lang.code === currentLang.code}
              >
                <span aria-hidden="true">{lang.flag}</span>
                <span>{lang.name}</span>
                <span className="appbar__lang-code">({lang.code.toUpperCase()})</span>
              </button>
            ))}
          </div>
        )}

        {menuOpen && (
          <div id="header-section-menu" className="appbar__sheet lg:hidden">
            <div className="appbar__sheet-top">
              <Link to="/" aria-label="LulPay Home" className="ui-logo-link" onClick={() => setMenuOpen(false)}>
                <Logo size="sm" />
              </Link>
              <button
                ref={closeButtonRef}
                type="button"
                className="appbar__menu-btn"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <nav aria-label="Sections">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="appbar__menu-item"
                    aria-current={currentSection === item.id ? true : undefined}
                  onClick={() => chooseSection(item.id)}
                >
                  {t(item.labelKey)}
                </button>
              ))}
            </nav>
            <button
              type="button"
              className="btn btn--primary appbar__sheet-cta"
              onClick={() => chooseSection('download')}
            >
              {t('nav.getApp')}
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
